import { computed, ref, watch } from "vue";
import type { announceById } from "~/utils/interface/announce";
import type { useSearchStore } from "~/store/useSearchHome";
import {
  countPromotedInNormal,
  getDisplayedPromotedIds,
  getPromotedDisplayAnnouncements,
  getUniquePromotedAnnouncements,
  mergePromotedAnnouncements,
} from "~/utils/promotedResults";

type SearchStore = ReturnType<typeof useSearchStore>;

export const usePromotedSearchResults = (searchStore: SearchStore) => {
  const runTimeConfig = useRuntimeConfig();
  const promotedAnnouncements = computed<announceById[]>({
    get: () => searchStore.getPromotedSearchResult ?? [],
    set: (value) => {
      searchStore.setPromotedSearchResult(Array.isArray(value) ? value : []);
    },
  });

  const buildPromotedPayload = () => {
    let payload: Record<string, any> = {};
    const selectedMakes = Array.isArray(searchStore.userInput?.makes)
      ? searchStore.userInput.makes
      : [];
    const selectedModels = Array.isArray(searchStore.userInput?.models)
      ? searchStore.userInput.models
      : [];

    const makesIds = selectedMakes
      .map((make: any) => make?.id)
      .filter((id: any) => typeof id === "number");
    const modelsIds = selectedModels
      .map((model: any) => model?.id)
      .filter((id: any) => typeof id === "number");

    if (makesIds.length) {
      payload.makesIds = makesIds;
    }
    if (modelsIds.length) {
      payload.modelsIds = modelsIds;
    }
    payload = {
      ...payload,
      ...searchStore.filterUserSearchInput(searchStore.userInput),
    };

    // Exclude UI-only/default values so "no filters" mode stays deterministic.
    return Object.fromEntries(
      Object.entries(payload).filter(
        ([key, value]) => key !== "front" && value !== false
      )
    );
  };

  const hasActiveFilters = computed(
    () => Object.keys(buildPromotedPayload()).length > 0
  );

  const normalAnnouncements = computed(
    () => searchStore.getFetchResult?.announcements ?? []
  );

  const normalBatchSize = computed(() => 20);
  const promotedPerBatch = computed(() => (hasActiveFilters.value ? 10 : 2));
  const promotedNeededForLoadedNormal = computed(() => {
    const normalCount = normalAnnouncements.value.length;
    if (normalCount <= 0) return 0;
    return Math.ceil(normalCount / 20) * promotedPerBatch.value;
  });

  // API payload limit: fixed per call (2 without filters, 10 with filters).
  const promotedRequestLimit = computed(() => promotedPerBatch.value);

  // UI display limit
  const promotedDisplayLimit = computed(() => promotedNeededForLoadedNormal.value);

  const promotedPayloadKey = computed(() =>
    JSON.stringify(buildPromotedPayload())
  );

  const fetchPromotedAnnouncements = async (options?: {
    replace?: boolean;
    resetOldPromotedIds?: boolean;
  }) => {
    if (!process.client) return;
    const payload = buildPromotedPayload();
    const limit = promotedRequestLimit.value;
    const oldPromotedAnnouncementIds = options?.resetOldPromotedIds
      ? []
      : getDisplayedPromotedIds(
          promotedAnnouncements.value,
          normalAnnouncements.value
        );

    try {
      const response: any = await $fetch(
        "/announcements/promoted-search-result",
        {
          method: "POST",
          headers: {
            token: runTimeConfig.public.token,
          },
          body: { ...payload, limit, oldPromotedAnnouncementIds, },
          baseURL: runTimeConfig.public.baseUrl,
        }
      );

      const announcements = Array.isArray(response)
        ? response
        : response?.announcements ?? [];
      const uniqueAnnouncements = getUniquePromotedAnnouncements(announcements);

      if (options?.replace) {
        searchStore.setPromotedSearchResult(uniqueAnnouncements);
        return;
      }

      searchStore.appendPromotedSearchResult(uniqueAnnouncements);
    } catch (error) {
      console.error(error);
    }
  };

  const promotedDisplayAnnouncements = computed(() =>
    getPromotedDisplayAnnouncements(
      promotedAnnouncements.value,
      promotedDisplayLimit.value
    )
  );

  const stableMergedAnnouncements = ref<announceById[]>([]);
  const lastMergePayloadKey = ref<string>("");
  const lastNormalPrefixKey = ref<string>("");
  const lastNormalRawLength = ref<number>(0);

  const appendMergedBlock = (
    normalBlock: announceById[],
    seenIds: Set<string>,
    mode: "default" | "alternating-two-normal"
  ) => {
    const promotedBatch: announceById[] = [];
    for (const promotedItem of promotedDisplayAnnouncements.value) {
      const id = String(promotedItem.id);
      if (seenIds.has(id)) continue;
      promotedBatch.push(promotedItem);
      if (promotedBatch.length >= promotedPerBatch.value) break;
    }

    const mergedBlock = mergePromotedAnnouncements(
      normalBlock,
      promotedBatch,
      normalBatchSize.value,
      promotedPerBatch.value,
      mode
    );

    if (!mergedBlock.length) return;
    for (const item of mergedBlock) {
      const id = String(item.id);
      if (seenIds.has(id)) continue;
      stableMergedAnnouncements.value.push(item);
      seenIds.add(id);
    }
  };

  watch(
    [normalAnnouncements, promotedDisplayAnnouncements, hasActiveFilters],
    () => {
      const mode = hasActiveFilters.value
        ? "alternating-two-normal"
        : "default";

      const payloadKey = promotedPayloadKey.value;
      const normalPrefixKey = normalAnnouncements.value
        .slice(0, normalBatchSize.value)
        .map((item) => String(item?.id))
        .join(",");

      const shouldResetStableMerge =
        payloadKey !== lastMergePayloadKey.value ||
        normalAnnouncements.value.length < lastNormalRawLength.value ||
        normalPrefixKey !== lastNormalPrefixKey.value;

      if (shouldResetStableMerge) {
        stableMergedAnnouncements.value = [];
        lastNormalRawLength.value = 0;
      }

      const seenIds = new Set(
        stableMergedAnnouncements.value.map((item) => String(item.id))
      );

      // Promoted-only updates must not re-place already rendered cards.
      if (normalAnnouncements.value.length <= lastNormalRawLength.value) {
        lastMergePayloadKey.value = payloadKey;
        lastNormalPrefixKey.value = normalPrefixKey;
        return;
      }

      const newNormalRaw = normalAnnouncements.value.slice(
        lastNormalRawLength.value
      );
      const newUniqueNormal = newNormalRaw.filter((item) => {
        const id = String(item?.id);
        if (seenIds.has(id)) return false;
        return true;
      });

      for (let i = 0; i < newUniqueNormal.length; i += normalBatchSize.value) {
        const normalBlock = newUniqueNormal.slice(i, i + normalBatchSize.value);
        if (normalBlock.length) {
          appendMergedBlock(normalBlock, seenIds, mode);
        }
      }

      lastMergePayloadKey.value = payloadKey;
      lastNormalPrefixKey.value = normalPrefixKey;
      lastNormalRawLength.value = normalAnnouncements.value.length;
    },
    { immediate: true }
  );

  const promotedDuplicatesInNormal = computed(() =>
    countPromotedInNormal(
      normalAnnouncements.value,
      promotedDisplayAnnouncements.value
    )
  );

  const mergedTotalCount = computed(() => {
    const baseTotal =
      searchStore.getFetchResult?.totalCount || searchStore.getAnnounceCount || 0;
    return (
      baseTotal +
      promotedDisplayAnnouncements.value.length -
      promotedDuplicatesInNormal.value
    );
  });

  return {
    refreshPromotedAnnouncements: fetchPromotedAnnouncements,
    mergedAnnouncements: stableMergedAnnouncements,
    mergedTotalCount,
    promotedAnnouncements,
    promotedDisplayAnnouncements,
    hasActiveFilters,
  };
};
